import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import getAxiosInstance from 'api';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const UserAccessForm = ({
  isOpen,
  setIsOpen,
  fetchBoiReportHistory,
  roles,
  states,
}) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    state: '',
    role: '',
  });
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    Object.entries(form).forEach(([key, value]) => {
      if (!value) {
        newErrors[key] = 'Required';
      }
    });

    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async () => {
    if (validate()) {
      try {
        setLoading(true);
        const res = await getAxiosInstance(
          'businessPartner/registerSecondaryUser',
          'post',
          { ...form, isBOI: true }
        );
        if (res?.data?.codes === 200) {
          toast.success('User added successfully!');
          setIsOpen(false);
          fetchBoiReportHistory();
        } else {
          toast.error('User add failed');
        }
      } catch (err) {
        toast.error(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        state: '',
        role: '',
      });
      setErrors({});
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          p: 2,
          width: '100%',
          maxWidth: 500,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h3" fontWeight={600}>
            Create User Access Roles
          </Typography>
          <IconButton onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body1" fontWeight={600}>
            First Name
          </Typography>
          <TextField
            placeholder="Enter first name"
            fullWidth
            value={form.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />

          <Typography variant="body1" fontWeight={600}>
            Last Name
          </Typography>

          <TextField
            placeholder="Enter last name"
            fullWidth
            value={form.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />

          <Typography variant="body1" fontWeight={600}>
            Email
          </Typography>

          <TextField
            placeholder="ex. abc123@gmail.com"
            fullWidth
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
          />

          <Typography variant="body1" fontWeight={600}>
            State
          </Typography>

          <TextField
            select
            fullWidth
            value={form.state}
            onChange={(e) => handleChange('state', e.target.value)}
            error={!!errors.state}
            helperText={errors.state}
          >
            {states?.length &&
              states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
          </TextField>

          <Typography variant="body1" fontWeight={600}>
            Role
          </Typography>

          <TextField
            select
            fullWidth
            value={form.role}
            onChange={(e) => handleChange('role', e.target.value)}
            error={!!errors.role}
            helperText={errors.role}
          >
            {roles?.length &&
              roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
          </TextField>

          <Button
            variant="contained"
            fullWidth
            onClick={onSubmit}
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.2,
              borderRadius: '8px',
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.main,
              },
              fontWeight: 600,
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              'Create User'
            )}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default UserAccessForm;
